import '../../App.css';

import { useQuery } from '@apollo/client';
import { Pagination, TextInput } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { LOG_TYPE_PRETIFY, LOGS_PER_PAGE } from '../../config/constants';
import { GET_LOGS, GET_LOGS_COUNT } from '../../graphql/queries';
import LogDetailsModal from '../Modals/LogDetailsModal';
import GeneralTable from '../Tables/GeneralTable';
// import FilledBtn from '../Buttons/FilledBtn';
import H1 from '../Texts/H1';
import H2 from '../Texts/H2';

interface GetLogsResponse {
  logs: Log[];
}

export interface Log {
  id: string;
  user_id?: string;
  user?: string;
  message: any;
  timestamp: string;
  type: string;
  sublogs: Sublogs[];
}

export interface Sublogs {
  information?: string;
  timestamp?: string;
}

const generateMessage = (type: string, message: any, sublogs: Sublogs[]) => {
  const info = JSON.parse(message);
  switch (type) {
    case 'search_mass':
      if (info.failed) return `Error while searching in file: ${info.filename}`;
      return `File searched: ${info.filename} (${info.totalCount} contacts searched)`;
    case 'search_single':
      return `Searched for single contact: ${sublogs[0]?.information}`;
    case 'add_mass':
      return `Added ${sublogs.length} contacts to EMC Database`;
    case 'add_opt_out':
      // eslint-disable-next-line no-case-declarations
      const newContactOptout = JSON.parse(sublogs[0]?.information || '{}');
      return `New opt-out contact to EMC database: ${newContactOptout.phone} - ${newContactOptout.name} - ${newContactOptout.email}`;
    case 'add_single':
      // eslint-disable-next-line no-case-declarations
      const newContact = JSON.parse(sublogs[0]?.information || '{}');
      return `Added new contact to EMC database: ${
        newContact.phone || 'No Phone'
      } - ${newContact.name || 'No Name'} - ${newContact.email || 'No Email'}`;
    default:
      return ' - ';
  }
};

interface IFormFilters {
  name: string;
  phone: string;
}

export default function Logs() {
  const { watch, register, reset } = useForm<IFormFilters>({
    defaultValues: {
      name: '',
      phone: '',
    },
  });
  const [clickedLog, setClickedLog] = useState<Log | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [activePage, setPage] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);

  const { name, phone } = watch();

  const { loading: loadingPagination, refetch: refetchPagination } = useQuery(
    GET_LOGS_COUNT,
    {
      onCompleted: (data) =>
        setPageTotal(Math.ceil(data.logsCount / LOGS_PER_PAGE)),
    }
  );

  const mapLogs = (data: GetLogsResponse) => {
    return data.logs.map((value) => ({
      ...value,
      user: value.user || 'System',
      message: generateMessage(value.type, value.message, value.sublogs),
      type: (LOG_TYPE_PRETIFY as any)[value.type],
      time: new Date(value.timestamp).toLocaleString(),
      onclick: () => {
        setClickedLog(value);
      },
    }));
  };
  const { refetch, loading } = useQuery<GetLogsResponse>(GET_LOGS, {
    notifyOnNetworkStatusChange: true,
    variables: { limit: LOGS_PER_PAGE, offset: 0 },
    onCompleted: (data) => {
      setLogs(mapLogs(data));
    },
  });

  const memoData = useMemo(() => logs, [logs]);

  useEffect(() => {
    refetch({ offset: (activePage - 1) * LOGS_PER_PAGE });
  }, [activePage]);

  // 👇 NUEVO: estados debounced
  const [debouncedName, setDebouncedName] = useState(name);
  const [debouncedPhone, setDebouncedPhone] = useState(phone);

  // 👇 NUEVO: efecto de debounce (500 ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedName(name);
      setDebouncedPhone(phone);
    }, 500); // ajusta el tiempo a tu gusto
    return () => clearTimeout(t);
  }, [name, phone]);

  useEffect(() => {
    setPage(1);
    refetchPagination({ name: debouncedName, phone: debouncedPhone });
    refetch({ offset: 0, name: debouncedName, phone: debouncedPhone });
  }, [debouncedName, debouncedPhone, refetch, refetchPagination]);

  return (
    <>
      <LogDetailsModal
        opened={clickedLog !== null}
        close={() => setClickedLog(null)}
        log={clickedLog}
      />
      <div className="flex justify-between">
        <div className="flex">
          <H2 text="Log" fontSize="32px" fontWeight={400} />
          <div className="pl-2">
            <H1 text="History" fontSize="32px" />
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-x-2 mt-2">
        <TextInput
          placeholder="Search name"
          radius="xl"
          // error={getPhoneError(errors?.input?.type || '')}
          style={{ width: '242px', color: '#223C78' }}
          // rightSection={rightSection}
          {...register('name', {
            required: true,
            validate: (val) => {
              if (/^[a-z]+$/i.test(val)) return val.length >= 4;
              return val.length >= 10;
            },
          })}
        />
        <TextInput
          placeholder="Search phone"
          radius="xl"
          // error={getPhoneError(errors?.input?.type || '')}
          style={{ width: '242px', color: '#223C78' }}
          // rightSection={rightSection}
          {...register('phone', {
            required: true,
            validate: (val) => {
              if (/^[a-z]+$/i.test(val)) return val.length >= 4;
              return val.length >= 10;
            },
          })}
        />
        <button
          className="bg-[#DC2626] px-2 rounded-full text-white"
          type="button"
          onClick={() => reset({ name: '', phone: '' })}
        >
          X Reset Filters
        </button>
      </div>
      {/* <div className="pt-2">
        <Paragraph text="On this page you can compare your phone numbers with our database to find any DNC/TCPA numbers" />
      </div> */}
      <div className="pt-5">
        <GeneralTable
          titleOptions={[
            { text: 'Time', fontSize: '14px', accessor: 'time' },
            { text: 'User', fontSize: '14px', accessor: 'user' },
            { text: 'Type', fontSize: '14px', accessor: 'type' },
            { text: 'Message', fontSize: '14px', accessor: 'message' },
          ]}
          loading={loading}
          elements={memoData}
        />
        <div className="flex justify-center pb-10 pr-36 pt-5">
          {!loadingPagination && memoData.length > 0 && (
            <Pagination
              page={activePage}
              onChange={setPage}
              total={pageTotal}
              color="gray"
              size="sm"
              radius="md"
            />
          )}
        </div>
      </div>
    </>
  );
}
